import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/http";
import { zTimestamp } from "./_schemas";

function crud<S extends z.ZodType>(path: string, key: string, schema: S) {
  return {
    useList: () =>
      useQuery({
        queryKey: [key, "list"],
        queryFn: () => apiRequest<unknown>(path).then((r) => z.array(schema).parse(r)),
      }),
    useMutations: () => {
      const qc = useQueryClient();
      const invalidate = () => qc.invalidateQueries({ queryKey: [key] });
      return {
        create: useMutation({
          mutationFn: (body: Record<string, unknown>) =>
            apiRequest<unknown>(path, { method: "POST", body }),
          onSuccess: invalidate,
        }),
        update: useMutation({
          mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
            apiRequest<unknown>(`${path}/${id}`, { method: "PUT", body }),
          onSuccess: invalidate,
        }),
        remove: useMutation({
          mutationFn: (id: string) => apiRequest<void>(`${path}/${id}`, { method: "DELETE" }),
          onSuccess: invalidate,
        }),
      };
    },
  };
}

// ── Workspaces ──────────────────────────────────────────────────────────────
export const workspaceEntrySchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullish(),
  color: z.string().nullish(),
});
export const workspacesApi = crud("/workspaces", "settings-workspaces", workspaceEntrySchema);

// ── Users & Roles ───────────────────────────────────────────────────────────
export const userEntrySchema = z.object({
  id: z.string(),
  email: z.string(),
  displayName: z.string().nullish(),
  provider: z.string().nullish(),
  status: z.string().nullish(),
  roleId: z.string().nullish(),
  role: z.string().nullish(),
  mfaEnabled: z.boolean().nullish(),
  lastLoginAt: zTimestamp.nullish(),
});
export const usersApi = crud("/users", "settings-users", userEntrySchema);

export const roleEntrySchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullish(),
  permissions: z.string().nullish(),
  tenantId: z.string().nullish(),
});
export const rolesApi = crud("/roles", "settings-roles", roleEntrySchema);

// ── SSO providers ───────────────────────────────────────────────────────────
const ssoProviderEntrySchema = z
  .object({
    id: z.string(),
    name: z.string(),
    displayName: z.string().nullish(),
    enabled: z.boolean().nullish(),
    issuerUri: z.string().nullish(),
    entityId: z.string().nullish(),
    ssoUrl: z.string().nullish(),
  })
  .passthrough();
export const oidcApi = crud("/oidc-providers", "settings-oidc", ssoProviderEntrySchema);
export const samlApi = crud("/saml-providers", "settings-saml", ssoProviderEntrySchema);

export function useTestSsoProvider(kind: "oidc" | "saml") {
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<{ ok?: boolean; message?: string; error?: string }>(
        `/${kind}-providers/${id}/test`,
        { method: "POST" },
      ),
  });
}

// ── SMTP ────────────────────────────────────────────────────────────────────
const smtpSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    host: z.string().nullish(),
    port: z.number().nullish(),
    username: z.string().nullish(),
    fromAddress: z.string().nullish(),
    fromName: z.string().nullish(),
    useTls: z.boolean().nullish(),
    useSsl: z.boolean().nullish(),
    enabled: z.boolean().nullish(),
  })
  .passthrough();
export const smtpApi = crud("/smtp-configs", "settings-smtp", smtpSchema);

export function useTestSmtp() {
  return useMutation({
    mutationFn: ({ id, to }: { id: string; to?: string }) =>
      apiRequest<{ ok?: boolean; success?: boolean; message?: string }>(
        `/smtp-configs/${id}/test${to ? `?to=${encodeURIComponent(to)}` : ""}`,
        { method: "POST" },
      ),
  });
}

// ── Tenant quota ────────────────────────────────────────────────────────────
const quotaSchema = z
  .object({
    tenantId: z.string().nullish(),
    maxConcurrentPipelines: z.number().nullish(),
    maxApiCallsPerHour: z.number().nullish(),
    maxRecordsPerDay: z.number().nullish(),
    notes: z.string().nullish(),
    updatedAt: zTimestamp.nullish(),
  })
  .passthrough();
export type TenantQuota = z.infer<typeof quotaSchema>;

export function useTenantQuota() {
  return useQuery({
    queryKey: ["settings-quota"],
    queryFn: () => apiRequest<unknown>("/tenant-quotas").then((r) => quotaSchema.parse(r)),
  });
}

export function useUpdateTenantQuota() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiRequest<unknown>("/tenant-quotas", { method: "PUT", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["settings-quota"] }),
  });
}

// ── IP allowlist ────────────────────────────────────────────────────────────
export const ipEntrySchema = z.object({
  id: z.string(),
  cidr: z.string(),
  description: z.string().nullish(),
  enabled: z.boolean().nullish(),
});
export const ipAllowlistApi = crud("/security/ip-allowlist", "settings-ip", ipEntrySchema);

// ── Sessions ────────────────────────────────────────────────────────────────
export const sessionEntrySchema = z.object({
  id: z.string(),
  deviceName: z.string().nullish(),
  ipAddress: z.string().nullish(),
  userAgent: z.string().nullish(),
  lastActiveAt: zTimestamp.nullish(),
  createdAt: zTimestamp.nullish(),
});
export type SessionEntry = z.infer<typeof sessionEntrySchema>;

export function useSessions() {
  return useQuery({
    queryKey: ["settings-sessions"],
    queryFn: () =>
      apiRequest<unknown>("/auth/sessions").then((r) => z.array(sessionEntrySchema).parse(r)),
  });
}
export function useSessionMutations() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["settings-sessions"] });
  return {
    revoke: useMutation({
      mutationFn: (id: string) => apiRequest<void>(`/auth/sessions/${id}`, { method: "DELETE" }),
      onSuccess: invalidate,
    }),
    revokeAll: useMutation({
      mutationFn: () => apiRequest<void>("/auth/sessions", { method: "DELETE" }),
      onSuccess: invalidate,
    }),
  };
}

// ── SCIM token ──────────────────────────────────────────────────────────────
export function useScimToken() {
  return useQuery({
    queryKey: ["settings-scim"],
    queryFn: () =>
      apiRequest<unknown>("/scim-tokens")
        .then((r) =>
          z
            .object({ tokenPrefix: z.string().nullish(), createdAt: zTimestamp.nullish() })
            .passthrough()
            .parse(r),
        )
        .catch(() => null),
  });
}
export function useScimMutations() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["settings-scim"] });
  return {
    generate: useMutation({
      mutationFn: () =>
        apiRequest<{ token?: string }>("/scim-tokens/generate", { method: "POST", body: {} }),
      onSuccess: invalidate,
    }),
    revoke: useMutation({
      mutationFn: () => apiRequest<void>("/scim-tokens", { method: "DELETE" }),
      onSuccess: invalidate,
    }),
  };
}

// ── Branding ────────────────────────────────────────────────────────────────
export const brandingSchema = z
  .object({
    companyName: z.string().nullish(),
    logoUrl: z.string().nullish(),
    logoData: z.string().nullish(),
    faviconUrl: z.string().nullish(),
    primaryColor: z.string().nullish(),
    accentColor: z.string().nullish(),
    supportEmail: z.string().nullish(),
    supportUrl: z.string().nullish(),
    footerText: z.string().nullish(),
    hideUdipBrand: z.boolean().nullish(),
  })
  .partial()
  .passthrough();
export type Branding = z.infer<typeof brandingSchema>;

export function useBranding() {
  return useQuery({
    queryKey: ["settings-branding"],
    queryFn: () => apiRequest<unknown>("/branding").then((r) => brandingSchema.parse(r ?? {})),
  });
}
export function useUpdateBranding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Branding) => apiRequest<unknown>("/branding", { method: "PUT", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["settings-branding"] }),
  });
}

// ── Regions ─────────────────────────────────────────────────────────────────
export function useRegions() {
  return useQuery({
    queryKey: ["settings-regions"],
    queryFn: () =>
      apiRequest<unknown>("/regions").then((r) =>
        z.array(z.object({ name: z.string(), url: z.string().nullish() }).passthrough()).parse(r),
      ),
  });
}
export function useTenantRegion() {
  return useQuery({
    queryKey: ["settings-tenant-region"],
    queryFn: () =>
      apiRequest<unknown>("/regions/tenant")
        .then((r) =>
          z
            .object({
              preferredRegion: z.string().nullish(),
              dataResidency: z.string().nullish(),
              failoverRegion: z.string().nullish(),
              notes: z.string().nullish(),
            })
            .passthrough()
            .parse(r),
        )
        .catch(() => null),
  });
}

// ── Alert webhooks ──────────────────────────────────────────────────────────
export const webhookSchema = z.object({
  id: z.string(),
  name: z.string().nullish(),
  url: z.string().nullish(),
  format: z.string().nullish(),
  events: z.string().nullish(),
  enabled: z.boolean().nullish(),
});
export const webhooksApi = crud("/alert-webhooks", "settings-webhooks", webhookSchema);

// ── Encryption keys (read-only) ─────────────────────────────────────────────
export function useTenantKey() {
  return useQuery({
    queryKey: ["settings-tenant-key"],
    queryFn: () =>
      apiRequest<unknown>("/tenant-keys").then((r) =>
        z
          .object({
            tenantId: z.string().nullish(),
            keyProvider: z.string().nullish(),
            keyArn: z.string().nullish(),
            configured: z.boolean().nullish(),
            message: z.string().nullish(),
            rotatedAt: zTimestamp.nullish(),
          })
          .passthrough()
          .parse(r ?? {}),
      ),
  });
}
export function usePkiConfig() {
  return useQuery({
    queryKey: ["settings-pki"],
    queryFn: () =>
      apiRequest<unknown>("/pki/config")
        .then((r) => z.record(z.string(), z.unknown()).parse(r ?? {}))
        .catch(() => ({}) as Record<string, unknown>),
  });
}
